import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, Grid, CircularProgress, Alert, Button, Chip, LinearProgress,
  Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/NotificationsActive';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PageLayout from '../../components/common/PageLayout';
import SolarSiteDetail from './SolarSiteDetail';
import { VendorBadge, vendorLabel, VENDOR_SUPPORT } from './vendorMeta';
import { useAuth } from '../../contexts/AuthContext';
import { get, post } from '../../utils/api';

const kwh = (n) => `${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })} kWh`;
const kw = (w) => `${(Number(w || 0) / 1000).toLocaleString('en-US', { maximumFractionDigits: 2 })} kW`;
const statusColor = (s) => (s === 'fault' ? 'error' : s === 'warning' ? 'warning' : s === 'offline' ? 'default' : 'success');
const statusLabel = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Unknown');

// "Last updated" = when we last successfully pulled a reading for that unit. If it stops advancing,
// the monitoring link to that building is broken even if the last-known status still reads "normal".
const ageHours = (ts) => (ts ? (Date.now() - new Date(ts).getTime()) / 3600000 : Infinity);
// Pipeline pulls hourly 9am–7pm CT + a nightly sweep, so a healthy unit can be ~half a day old overnight.
// Beyond a full day without a fresh reading means the pull is failing for that unit.
const STALE_HOURS = 26;
const isStale = (ts) => ageHours(ts) > STALE_HOURS;
const fmtAgo = (ts) => {
  if (!ts) return 'never';
  const ms = Date.now() - new Date(ts).getTime();
  if (Number.isNaN(ms)) return '—';
  const m = Math.round(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

export default function SolarFleetPage() {
  const { staff } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [testing, setTesting] = useState(false);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [selected, setSelected] = useState(null); // site id for the detail dialog

  const load = useCallback(async () => {
    setErr('');
    try { setData(await get('/api/manage/solar/overview')); }
    catch (e) { setErr(e?.response?.data?.error || e?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true); setErr(''); setNote('');
    try {
      const r = await post('/api/manage/solar/run', {});
      const vendors = (r?.vendors || []).map((v) => `${v.vendor} ${v.sites}`).join(' · ');
      const errs = (r?.errors || []);
      setNote(`Pulled ${r?.sites ?? 0} site(s)${vendors ? ` — ${vendors}` : ''}.${errs.length ? ` ${errs.length} issue(s): ${errs.slice(0, 3).join('; ')}` : ''}`);
      await load();
    } catch (e) { setErr(e?.response?.data?.error || e?.message || 'Refresh failed'); }
    finally { setRunning(false); }
  };

  const testAlert = async () => {
    setTesting(true); setErr(''); setNote('');
    try {
      const r = await post('/api/manage/solar/alert', { test_to: staff?.email });
      setNote(r?.sent ? `Test alert emailed to ${(r.to || []).join(', ')} (${r.count} issue(s) listed).` : `No email sent (${r?.reason || 'nothing to report'}).`);
    } catch (e) { setErr(e?.response?.data?.error || e?.message || 'Test alert failed'); }
    finally { setTesting(false); }
  };

  const totals = data?.totals || {};
  const sites = useMemo(() => data?.sites || [], [data]);
  const freshest = useMemo(() => sites.reduce((m, s) => {
    const t = s.updated_at ? new Date(s.updated_at).getTime() : 0;
    return t > m ? t : m;
  }, 0), [sites]);
  const faults = sites.filter((s) => s.status === 'fault');
  const lifetimeMwh = (Number(totals.lifetime_kwh || 0) / 1000);
  const buildingCount = data?.count ?? sites.length;
  const nameOf = (s) => s.name || s.building || s.label;

  const stat = (label, value, sub, color) => (
    <Paper variant="outlined" sx={{ p: 2, height: '100%', borderTop: 3, borderTopColor: color ? `${color}.main` : 'divider' }}>
      <Typography variant="caption" sx={{ color: color ? `${color}.dark` : 'text.secondary', fontWeight: 600 }}>{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, color: color ? `${color}.dark` : 'text.primary' }}>{value}</Typography>
      {sub != null && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{sub}</Typography>}
    </Paper>
  );

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <WbSunnyIcon sx={{ color: 'warning.main' }} /> Solar monitoring
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              ICCT campus fleet · Eads, TN
              {freshest ? ` · updated ${fmtAgo(freshest)}` : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button startIcon={testing ? <CircularProgress size={16} /> : <NotificationsIcon />} variant="text" size="small"
              onClick={testAlert} disabled={testing || !staff?.email}>
              {testing ? 'Sending…' : 'Test alert'}
            </Button>
            <Button startIcon={running ? <CircularProgress size={16} /> : <RefreshIcon />} variant="outlined" onClick={runNow} disabled={running}>
              {running ? 'Refreshing…' : 'Refresh now'}
            </Button>
          </Box>
        </Box>

        {err && <Alert severity="error" sx={{ my: 2 }} onClose={() => setErr('')}>{err}</Alert>}
        {note && <Alert severity={/issue/i.test(note) ? 'warning' : 'success'} sx={{ my: 2 }} onClose={() => setNote('')}>{note}</Alert>}

        {loading ? <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={26} /></Box> : (
          <>
            <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
              <Grid item xs={6} md={3}>{stat('Producing now', kw(totals.power_w), 'all buildings', 'primary')}</Grid>
              <Grid item xs={6} md={3}>{stat('Today', kwh(totals.today_kwh), 'production', null)}</Grid>
              <Grid item xs={6} md={3}>{stat('Lifetime', `${lifetimeMwh.toLocaleString('en-US', { maximumFractionDigits: 2 })} MWh`, 'all time', null)}</Grid>
              <Grid item xs={6} md={3}>{stat('Buildings', `${buildingCount}`, `${totals.normal || 0} normal · ${totals.fault || 0} fault`, (totals.fault ? 'error' : 'success'))}</Grid>
            </Grid>

            {faults.map((f) => (
              <Alert key={f.id} severity="error" icon={<WarningAmberIcon />} sx={{ mb: 1.5 }}
                action={<Button color="inherit" size="small" onClick={() => setSelected(f.id)}>Details</Button>}>
                <strong>{nameOf(f)}{f.building ? ` (${f.building})` : ''} — PV fault.</strong> {vendorLabel(f.vendor)} · {Number(f.power_w || 0) === 0 ? '0 W' : kw(f.power_w)} now
                {f.battery_soc != null ? ` · running on battery (${Math.round(f.battery_soc)}%) + grid` : ''}
              </Alert>
            ))}

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2, mt: 0.5 }}>
              {sites.map((s) => (
                <Paper key={s.id} variant="outlined" onClick={() => setSelected(s.id)}
                  sx={{ p: 2, height: '100%', cursor: 'pointer', borderColor: s.status === 'fault' ? 'error.light' : 'divider',
                    transition: '.12s', '&:hover': { boxShadow: 3, borderColor: 'primary.light' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap title={nameOf(s)}>{nameOf(s)}</Typography>
                      {s.building && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{s.building}</Typography>}
                      {s.label && s.label !== nameOf(s) && (
                        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }} noWrap title={s.label}>
                          “{s.label}”
                        </Typography>
                      )}
                    </Box>
                    <VendorBadge vendor={s.vendor} showLabel={false} size={26} />
                  </Box>
                  <Chip size="small" color={statusColor(s.status)} label={statusLabel(s.status)} sx={{ mb: 1.5 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
                    {s.power_w != null ? kw(s.power_w) : '—'} <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>now</Typography>
                  </Typography>
                  <Box sx={{ mt: 1.5, display: 'grid', gap: 0.5 }}>
                    <Row label="Today" value={kwh(s.today_kwh)} />
                    <Row label="Lifetime" value={Number(s.lifetime_kwh || 0) >= 1000 ? `${(s.lifetime_kwh / 1000).toFixed(2)} MWh` : kwh(s.lifetime_kwh)} />
                    {s.has_battery ? (
                      <Box sx={{ mt: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Battery</Typography>
                          <Typography variant="caption">{s.battery_soc != null ? `${Math.round(s.battery_soc)}%` : '—'}</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, Number(s.battery_soc || 0)))}
                          color={Number(s.battery_soc || 0) < 20 ? 'warning' : 'success'} sx={{ height: 5, borderRadius: 3, mt: 0.3 }} />
                      </Box>
                    ) : <Row label="Battery" value="PV only" muted />}
                  </Box>
                  {(() => {
                    const stale = isStale(s.updated_at);
                    return (
                      <Box sx={{ mt: 1.25, pt: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 0.5 }}
                        title={s.updated_at ? `Last successful data pull: ${new Date(s.updated_at).toLocaleString()}` : 'No reading recorded yet'}>
                        {stale ? <WarningAmberIcon sx={{ fontSize: 13, color: 'warning.main' }} /> : <AccessTimeIcon sx={{ fontSize: 13, color: 'text.disabled' }} />}
                        <Typography variant="caption" sx={{ color: stale ? 'warning.main' : 'text.secondary', fontWeight: stale ? 700 : 400 }}>
                          {s.updated_at ? `Updated ${fmtAgo(s.updated_at)}${stale ? ' · stale' : ''}` : 'No data yet'}
                        </Typography>
                      </Box>
                    );
                  })()}
                </Paper>
              ))}
            </Box>

            {!sites.length && <Alert severity="info" sx={{ mt: 2 }}>No sites yet. Click “Refresh now” to pull the first readings from the vendor clouds.</Alert>}

            <Box sx={{ mt: 3 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary' }}>Vendor support</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 2, mt: 0.5 }}>
                {['apsystems', 'solark', 'eg4'].map((v) => {
                  const sup = VENDOR_SUPPORT[v] || {};
                  return (
                    <Paper key={v} variant="outlined" sx={{ p: 1.5 }}>
                      <Box sx={{ mb: 0.5 }}><VendorBadge vendor={v} size={22} /></Box>
                      <Typography variant="caption" sx={{ display: 'block' }}><strong>Phone:</strong> <span style={{ userSelect: 'all' }}>{sup.phone}</span></Typography>
                      <Typography variant="caption" sx={{ display: 'block' }}><strong>Email:</strong> <span style={{ userSelect: 'all' }}>{sup.email}</span></Typography>
                      {sup.email2 && <Typography variant="caption" sx={{ display: 'block' }}><strong>Tech:</strong> <span style={{ userSelect: 'all' }}>{sup.email2}</span></Typography>}
                      {sup.loginUrl && <Typography variant="caption" sx={{ display: 'block' }}><strong>Portal:</strong> <a href={sup.loginUrl} target="_blank" rel="noopener noreferrer">{sup.portal || 'Login'} ↗</a></Typography>}
                    </Paper>
                  );
                })}
              </Box>
            </Box>

            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 2 }}>
              Data refreshed hourly during the day (9am–7pm CT) plus a nightly pull; use “Refresh now” for the latest.
              Open a building for its support contact and the IDs to quote. Sources: APsystems (official API), Sol-Ark (MySolArk), EG4 (Luxpower).
            </Typography>
          </>
        )}
      </Box>

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          Building details
          <IconButton onClick={() => setSelected(null)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selected && <SolarSiteDetail siteId={selected} />}
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}

function Row({ label, value, muted }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
      <Typography variant="caption" sx={{ color: muted ? 'text.disabled' : 'text.primary', fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
    </Box>
  );
}
